import React, { useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { productService } from '../services/product.service';
import { storeService } from '../services/store.service';
import { Product } from '../types';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from '../components/ui/dropdown-menu';
import {
  ChevronLeft,
  ChevronRight,
  ShoppingBag,
  Star,
  X,
  Plus,
  Filter,
  Tag,
  Search as SearchIcon
} from 'lucide-react';
import { Input } from '../components/ui/input';

interface ActiveFilter {
  type: 'category' | 'search' | 'attribute';
  value: string;
  label?: string;
}

const ATTRIBUTE_OPTIONS = ['recommended', 'discount', 'new-arrival', 'best-seller', 'trending', 'clearance', 'featured'];

function filtersFromParams(searchParams: URLSearchParams): ActiveFilter[] {
  const filters: ActiveFilter[] = [];

  const category = searchParams.get('category');
  if (category) {
    filters.push({ type: 'category', value: category, label: `Category: ${category}` });
  }

  const q = searchParams.get('q');
  if (q) {
    filters.push({ type: 'search', value: q, label: `Search: ${q}` });
  }

  searchParams.getAll('attributes').forEach(attr => {
    filters.push({ type: 'attribute', value: attr, label: attr });
  });

  return filters;
}

function filtersToParams(filters: ActiveFilter[]): URLSearchParams {
  const params = new URLSearchParams();
  filters.forEach(filter => {
    if (filter.type === 'category') params.set('category', filter.value);
    if (filter.type === 'search') params.set('q', filter.value);
    if (filter.type === 'attribute') params.append('attributes', filter.value);
  });
  return params;
}

export const ProductListingPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');

  const { data: categories } = useQuery({
    queryKey: ['categories'],
    queryFn: storeService.getCategories
  });

  const activeFilters = useMemo(() => {
    const filters = filtersFromParams(searchParams);
    if (categories) {
      filters.forEach(f => {
        if (f.type === 'category') {
          const cat = categories.find(c => c.slug === f.value);
          if (cat) f.label = `Category: ${cat.name}`;
        }
      });
    }
    return filters;
  }, [searchParams, categories]);

  const updateFilters = (newFilters: ActiveFilter[]) => {
    setPage(1);
    setSearchParams(filtersToParams(newFilters), { replace: true });
  };

  const addFilter = (type: ActiveFilter['type'], value: string, displayLabel?: string) => {
    const current = [...activeFilters];

    if (type === 'category' || type === 'search') {
      const filtered = current.filter(f => f.type !== type);
      filtered.push({
        type,
        value,
        label: type === 'search' ? `Search: ${value}` : `Category: ${displayLabel || value}`
      });
      updateFilters(filtered);
      return;
    }

    const normalizedValue = value.toLowerCase();
    if (current.some(f => f.type === 'attribute' && f.value === normalizedValue)) return;

    current.push({ type: 'attribute', value: normalizedValue, label: normalizedValue });
    updateFilters(current);
  };

  const removeFilter = (filterToRemove: ActiveFilter) => {
    updateFilters(activeFilters.filter(f =>
      !(f.type === filterToRemove.type && f.value === filterToRemove.value)
    ));
  };

  const queryParams = useMemo(() => ({
    page,
    size: 12,
    search: activeFilters.find(f => f.type === 'search')?.value,
    category: activeFilters.find(f => f.type === 'category')?.value,
    attributes: activeFilters.filter(f => f.type === 'attribute').map(f => f.value),
  }), [page, activeFilters]);

  const { data, isLoading: loading, error } = useQuery({
    queryKey: ['products', queryParams],
    queryFn: () => productService.getProducts(queryParams),
  });

  if (loading && !data) return <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20 flex justify-center"><LoadingSpinner /></div>;

  if (error) return (
    <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20 text-center space-y-4">
      <h2 className="text-2xl font-black text-destructive tracking-tight">Error Loading Products</h2>
      <p className="text-muted-foreground">Unable to fetch the collection at this time.</p>
      <Button onClick={() => window.location.reload()} className="rounded-xl">Retry Connection</Button>
    </div>
  );

  const products = data?.content || [];
  const activeAttributes = activeFilters.filter(f => f.type === 'attribute').map(f => f.value);

  return (
    <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-6 md:py-10">

      {/* Filter Bar */}
      <div className="flex flex-wrap items-center gap-3 mb-10">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="h-9 rounded-full px-4 border-dashed border-2 gap-2 font-bold hover:bg-secondary/50">
              <Plus className="size-4" />
              Add Filter
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56">
            <DropdownMenuLabel>Filter By</DropdownMenuLabel>
            <DropdownMenuSeparator />

            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <Filter className="mr-2 size-4" />
                Category
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="w-48">
                {categories?.map((cat) => (
                  <DropdownMenuItem key={cat.id} onClick={() => addFilter('category', cat.slug, cat.name)}>
                    {cat.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>

            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <SearchIcon className="mr-2 size-4" />
                Search
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="p-2 w-60">
                <div className="flex gap-2">
                  <Input
                    placeholder="Keywords..."
                    className="h-8"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && searchTerm.trim()) {
                        addFilter('search', searchTerm.trim());
                        setSearchTerm('');
                      }
                    }}
                  />
                  <Button size="sm" className="h-8 px-2" onClick={() => {
                    if (searchTerm.trim()) {
                      addFilter('search', searchTerm.trim());
                      setSearchTerm('');
                    }
                  }}>OK</Button>
                </div>
              </DropdownMenuSubContent>
            </DropdownMenuSub>

            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <Tag className="mr-2 size-4" />
                Attributes
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="w-48">
                {ATTRIBUTE_OPTIONS.map((attr) => (
                  <DropdownMenuItem
                    key={attr}
                    onClick={() => addFilter('attribute', attr)}
                    disabled={activeAttributes.includes(attr)}
                  >
                    <span className="capitalize">{attr.replace(/-/g, ' ')}</span>
                    {activeAttributes.includes(attr) && (
                      <span className="ml-auto text-xs text-muted-foreground">✓</span>
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          </DropdownMenuContent>
        </DropdownMenu>

        {activeFilters.length > 0 && (
          <div className="h-6 w-px bg-border mx-1 hidden sm:block" />
        )}

        <div className="flex flex-wrap gap-2">
          {activeFilters.map((filter) => (
            <Badge
              key={`${filter.type}-${filter.value}`}
              variant="secondary"
              className="h-9 px-3 rounded-full gap-2 text-sm font-medium hover:bg-secondary"
            >
              {filter.label}
              <button
                onClick={() => removeFilter(filter)}
                className="rounded-full p-0.5 hover:bg-background/50 transition-colors"
              >
                <X className="size-3" />
              </button>
            </Badge>
          ))}
          {activeFilters.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => updateFilters([])}
              className="h-9 rounded-full text-muted-foreground hover:text-destructive hover:bg-destructive/10"
            >
              Clear all
            </Button>
          )}
        </div>
      </div>

      {products.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 md:gap-10">
          {products.map((prod: Product) => (
            <div key={prod.id} className="group space-y-4 transition-all hover:-translate-y-1">
              <div className="relative aspect-square rounded-[2rem] bg-gray-50 dark:bg-gray-900 overflow-hidden shadow-sm group-hover:shadow-2xl group-hover:shadow-primary/10 transition-all duration-500">
                <Link to={`/products/${prod.productCode}`}>
                  <img
                    alt={prod.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out"
                    src={prod.thumbnailUrl || (prod.images && prod.images[0]?.url) || 'https://via.placeholder.com/400'}
                  />
                </Link>
                <button className="absolute top-5 right-5 size-11 flex items-center justify-center rounded-full bg-white/90 dark:bg-black/80 text-[#111418] dark:text-white backdrop-blur-md opacity-0 group-hover:opacity-100 transition-all focus:opacity-100 hover:bg-primary hover:text-white shadow-xl translate-y-2 group-hover:translate-y-0">
                  <span className="material-symbols-outlined text-xl">favorite</span>
                </button>
                <div className="absolute bottom-5 left-5 right-5 translate-y-4 group-hover:translate-y-0 transition-all duration-500 px-2 opacity-0 group-hover:opacity-100">
                  <button className="w-full bg-primary text-white py-4 rounded-2xl font-black text-xs shadow-2xl shadow-primary/30 active:scale-95 transition-transform flex items-center justify-center gap-2">
                    <ShoppingBag className="size-4" /> Quick Add
                  </button>
                </div>
              </div>
              <div className="flex flex-col items-center text-center space-y-1.5 px-2">
                <div className="flex items-center gap-2">
                  <p className="text-[10px] font-black uppercase text-primary/60 tracking-[0.2em] font-display">
                    {prod.category}
                  </p>
                  {prod.rating && (
                    <div className="flex items-center gap-1 opacity-60">
                      <Star className="size-2.5 fill-current text-yellow-500" />
                      <span className="text-[9px] font-bold">{prod.rating}</span>
                    </div>
                  )}
                </div>
                <h3 className="text-lg font-bold text-[#111418] dark:text-white hover:text-primary transition-colors cursor-pointer line-clamp-1">
                  <Link to={`/products/${prod.productCode}`}>{prod.name}</Link>
                </h3>
                <p className="text-xl font-black text-[#111418] dark:text-whiteTracking-tight">${prod.price}</p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="py-32 text-center space-y-6">
          <div className="size-20 bg-gray-50 dark:bg-gray-900 rounded-[2.5rem] flex items-center justify-center mx-auto mb-6">
            <SearchIcon className="size-8 text-gray-300" />
          </div>
          <p className="text-2xl font-black tracking-tight text-gray-400">No treasures found.</p>
          <Button variant="link" onClick={() => updateFilters([])} className="text-primary font-bold">Clear filters</Button>
        </div>
      )}

      {data && data.totalPages > 1 && (
        <div className="mt-20 flex items-center justify-center gap-6">
          <Button
            variant="ghost"
            size="lg"
            onClick={() => setPage(page - 1)}
            disabled={page === 1}
            className="rounded-2xl gap-2 font-bold hover:bg-gray-50 dark:hover:bg-gray-900"
          >
            <ChevronLeft className="h-5 w-5" /> Previous
          </Button>
          <div className="text-sm font-black text-gray-400 uppercase tracking-widest">
            Page {page} of {data.totalPages}
          </div>
          <Button
            variant="ghost"
            size="lg"
            onClick={() => setPage(page + 1)}
            disabled={page === data.totalPages}
            className="rounded-2xl gap-2 font-bold hover:bg-gray-50 dark:hover:bg-gray-900"
          >
            Next <ChevronRight className="h-5 w-5" />
          </Button>
        </div>
      )}
    </div>
  );
};
